import React from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Divider,
  Avatar,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Link
} from '@mui/material';
import {
  Directions as DirectionsIcon,
  Bookmark as SaveIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Send as SendIcon,
  NearMe as NearbyIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';

const BusinessProfilePreview = ({ businessData, reviewsData, compact = false }) => {
  const [selectedTab, setSelectedTab] = React.useState(0);

  if (!businessData) {
    return (
      <Card sx={{ maxWidth: compact ? 400 : 500, height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Keine Geschäftsdaten verfügbar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatBusinessHours = (hours) => {
    if (!hours || !hours.open_hours) return 'Öffnungszeiten nicht verfügbar';
    
    const now = new Date();
    const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
    const todayHours = hours.open_hours[currentDay];
    
    if (!todayHours || !todayHours.intervals || todayHours.intervals.length === 0) {
      return 'Heute geschlossen';
    }
    
    const interval = todayHours.intervals[0];
    return `Geöffnet ⋅ Schließt um ${interval.close}`;
  };

  const getSocialMediaLinks = () => {
    const links = [];
    if (businessData.attributes) {
      if (businessData.attributes['attributes/url_facebook']) {
        links.push({ icon: <FacebookIcon />, url: businessData.attributes['attributes/url_facebook'], name: 'Facebook' });
      }
      if (businessData.attributes['attributes/url_instagram']) {
        links.push({ icon: <InstagramIcon />, url: businessData.attributes['attributes/url_instagram'], name: 'Instagram' });
      }
      if (businessData.attributes['attributes/url_linkedin']) {
        links.push({ icon: <LinkedInIcon />, url: businessData.attributes['attributes/url_linkedin'], name: 'LinkedIn' });
      }
      if (businessData.attributes['attributes/url_youtube']) {
        links.push({ icon: <YouTubeIcon />, url: businessData.attributes['attributes/url_youtube'], name: 'YouTube' });
      }
    }
    return links;
  };

  return (
    <Card 
      sx={{ 
        maxWidth: compact ? 400 : 500, 
        height: 'fit-content',
        border: '1px solid amDesignColors.border',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: 2
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header with business photo */}
        <Box sx={{ position: 'relative', height: compact ? 120 : 160, overflow: 'hidden' }}>
          {businessData.media_cover || businessData.media_profile ? (
            <img
              src={businessData.media_cover?.proxyGoogleUrl || businessData.media_cover?.googleUrl || 
                   businessData.media_cover?.proxySourceUrl || businessData.media_cover?.sourceUrl || 
                   businessData.media_profile?.proxyGoogleUrl || businessData.media_profile?.googleUrl || 
                   businessData.media_profile?.proxySourceUrl || businessData.media_profile?.sourceUrl}
              alt={businessData.business_name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <Box 
              sx={{ 
                width: '100%', 
                height: '100%', 
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Kein Titelbild
              </Typography>
            </Box>
          )}
          
          {/* Profile avatar */}
          <Avatar
            src={businessData.media_profile?.proxyGoogleUrl || businessData.media_profile?.googleUrl || 
                 businessData.media_profile?.proxySourceUrl || businessData.media_profile?.sourceUrl}
            sx={{
              position: 'absolute',
              bottom: -25,
              left: 16,
              width: 50,
              height: 50,
              border: '3px solid white',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              bgcolor: amDesignColors.primary
            }}
          >
            {businessData.business_name?.charAt(0) || 'B'}
          </Avatar>
        </Box>

        {/* Business Info Section */}
        <Box sx={{ px: 2, pt: 3.5, pb: 2 }}>
          {/* Business Name */}
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ 
              fontSize: compact ? '1.1rem' : '1.25rem',
              mb: 0.5,
              lineHeight: 1.2
            }}
          >
            {businessData.business_name}
          </Typography>

          {/* Rating and Reviews */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Rating 
              value={reviewsData?.statistics?.averageRating || businessData.rating || businessData.reviews?.avg_rating || 0} 
              precision={0.1} 
              size="small" 
              readOnly 
            />
            <Typography variant="body2" color="text.secondary">
              {(reviewsData?.statistics?.averageRating || businessData.rating || businessData.reviews?.avg_rating || 0).toFixed(1)} 
              ({reviewsData?.statistics?.totalReviews || businessData.reviews?.count || businessData.userRatingsTotal || 0} Bewertungen)
            </Typography>
          </Box>

          {/* Category */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {businessData.categories?.[0]?.displayName || 
             businessData.primary_category?.displayName || 
             'Kategorie nicht verfügbar'}
          </Typography>


          {/* Tabs */}
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: '1px solid amDesignColors.border', mb: 2 }}
          >
            <Tab label="Übersicht" sx={{ textTransform: 'none', fontSize: '0.8rem' }} />
            <Tab label="Bewertungen" sx={{ textTransform: 'none', fontSize: '0.8rem' }} />
            <Tab label="Info" sx={{ textTransform: 'none', fontSize: '0.8rem' }} />
          </Tabs>

          {/* Content based on selected tab */}
          {selectedTab === 0 && (
            <Box>
              {/* Business Hours */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {formatBusinessHours(businessData)}
                </Typography>
              </Box>

              {/* Address */}
              {businessData.business_address && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {businessData.business_address}
                  </Typography>
                </Box>
              )}

              {/* Phone */}
              {businessData.phone_numbers?.[0] && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Link 
                    href={`tel:${businessData.phone_numbers[0]}`}
                    variant="body2"
                    underline="hover"
                  >
                    {businessData.phone_numbers[0]}
                  </Link>
                </Box>
              )}

              {/* Website */}
              {businessData.website_uri && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WebsiteIcon fontSize="small" color="action" />
                  <Link 
                    href={businessData.website_uri}
                    target="_blank"
                    variant="body2"
                    underline="hover"
                  >
                    Website besuchen
                  </Link>
                </Box>
              )}

              {/* Description */}
              {businessData.business_description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {businessData.business_description}
                  </Typography>
                </Box>
              )}

              {/* Social Media Links */}
              {getSocialMediaLinks().length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Social Media
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {getSocialMediaLinks().map((social, index) => (
                      <IconButton
                        key={index}
                        size="small"
                        component="a"
                        href={social.url}
                        target="_blank"
                        sx={{ color: amDesignColors.primary }}
                      >
                        {social.icon}
                      </IconButton>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {selectedTab === 1 && (
            <Box>
              {reviewsData?.statistics ? (
                <Stack spacing={2}>
                  {/* Overall Statistics */}
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: amDesignColors.ghost, borderRadius: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#C41E3A', mb: 1 }}>
                      {reviewsData.statistics.averageRating.toFixed(1)}
                    </Typography>
                    <Rating value={reviewsData.statistics.averageRating} readOnly precision={0.1} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {reviewsData.statistics.totalReviews} Bewertungen gesamt
                    </Typography>
                  </Box>

                  {/* Key Metrics */}
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Antwortrate:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {reviewsData.statistics.responseRate}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Unbeantwortete:</Typography>
                      <Typography variant="body2" fontWeight="bold" 
                        color={reviewsData.statistics.unansweredReviews > 0 ? 'error.main' : 'success.main'}>
                        {reviewsData.statistics.unansweredReviews}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Neue (7 Tage):</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {reviewsData.statistics.newReviews}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Rating Distribution Preview */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Verteilung
                    </Typography>
                    <Stack spacing={0.5}>
                      {Object.entries(reviewsData.statistics.ratingDistribution)
                        .reverse()
                        .slice(0, 3)
                        .map(([stars, count]) => {
                          const total = Object.values(reviewsData.statistics.ratingDistribution).reduce((sum, val) => sum + val, 0);
                          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                          return (
                            <Box key={stars} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ minWidth: 12 }}>
                                {stars}★
                              </Typography>
                              <Box sx={{ 
                                flexGrow: 1, 
                                height: 4, 
                                bgcolor: amDesignColors.border, 
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  height: '100%',
                                  width: `${percentage}%`,
                                  bgcolor: '#C41E3A',
                                  borderRadius: 2
                                }} />
                              </Box>
                              <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'right', fontSize: '0.75rem' }}>
                                {count}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Bewertungsdaten werden geladen...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {selectedTab === 2 && (
            <Box>
              <Stack spacing={2}>
                {/* Business Contact Info */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Kontaktinformationen
                  </Typography>
                  <Stack spacing={1}>
                    {businessData.phone_numbers?.[0] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {businessData.phone_numbers[0]}
                        </Typography>
                      </Box>
                    )}
                    {businessData.website_uri && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WebsiteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {businessData.website_uri}
                        </Typography>
                      </Box>
                    )}
                    {businessData.business_address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {businessData.business_address}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Review Summary */}
                {reviewsData?.statistics && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Bewertungs-Zusammenfassung
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Gesamtbewertung:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {reviewsData.statistics.averageRating.toFixed(1)} / 5.0
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Anzahl Bewertungen:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {reviewsData.statistics.totalReviews}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Antwortrate:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {reviewsData.statistics.responseRate}%
                        </Typography>
                      </Box>
                      {reviewsData.statistics.unansweredReviews > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="warning.main">
                            Benötigen Antwort:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="warning.main">
                            {reviewsData.statistics.unansweredReviews}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Business Status */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Status
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Verifiziert:</Typography>
                      <Typography variant="body2" fontWeight="bold" 
                        color={businessData.is_verified ? 'success.main' : 'warning.main'}>
                        {businessData.is_verified ? 'Ja' : 'Nein'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Kategorien:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {businessData.categories?.length || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Bilder:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {businessData.media_items?.length || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BusinessProfilePreview;